// Vulnerabilidad POR AMENAZA (punto 17 / PE-19, resuelto por Claude el 27/10/2026; Kevin solo revisa).
// Criterios de preparación en 3 dimensiones: personas, recursos y sistemas/procesos. Comunes a toda amenaza
// más los específicos de sismo, inundación y volcán ("otra" usa solo los comunes).
//  - Calificación: cumple 1; parcial 0,5; no cumple 0; no aplica, fuera del denominador.
//  - Los criterios que ya resuelve pe-verificacion.ts se llenan solos desde sus trazas (y ganan a la
//    respuesta del responsable); el resto lo responde el responsable.
//  - % → nivel: ≥ 90 baja; 70 a 89,99 media; 50 a 69,99 alta; < 50 muy alta.
//  - El responsable puede SUBIR la vulnerabilidad con justificación obligatoria; nunca bajarla.
//  - La matriz 4×4 del plan de referencia no cambia (pe-constantes-plan-referencia.ts).
// Dónde se guarda (sin migración): emergency_assessments, método 'amenaza_vulnerabilidad', nivel plan
// (sector_id null). Como hay una sola fila por plan y método, inputs.vulnerabilidad es un objeto por
// amenaza: { sismo: { respuestas, ajuste }, inundacion: {...}, ... }; las trazas van en traces.

import { etiquetaNivel, Nivel4 } from './pe-constantes-plan-referencia';
import { doc, Traza } from './pe-tipos';

const METODO = 'amenaza_vulnerabilidad';

export type Dimension = 'personas' | 'recursos' | 'sistemas';
export type TipoAmenazaVulnerabilidad = 'sismo' | 'inundacion' | 'volcan' | 'otra';
export type Respuesta = 'cumple' | 'parcial' | 'no_cumple' | 'no_aplica';

export const ETIQUETA_DIMENSION: Record<Dimension, string> = {
  personas: 'Personas', recursos: 'Recursos', sistemas: 'Sistemas y procesos',
};

export interface CriterioVulnerabilidad {
  id: string;
  texto: string;
  dimension: Dimension;
  amenazas: TipoAmenazaVulnerabilidad[] | 'todas';
  // Trazas de pe-verificacion.ts que lo resuelven (por artículo o tabla).
  verificacion?: (t: Traza) => boolean;
}

const deVerificacion = (...articulos: string[]) => (t: Traza) => t.metodo === 'verificacion' && articulos.includes(t.articulo_o_tabla);

export const CRITERIOS_VULNERABILIDAD: CriterioVulnerabilidad[] = [
  // Comunes: personas
  { id: 'brigada', texto: 'Brigada conformada y capacitada', dimension: 'personas', amenazas: 'todas',
    verificacion: deVerificacion('Art. 169', '11.5 (i)', 'Brigada capacitada') },
  { id: 'simulacro_12m', texto: 'Simulacro en los últimos 12 meses', dimension: 'personas', amenazas: 'todas' },
  { id: 'capacitacion_plan', texto: 'Trabajadores capacitados en el plan', dimension: 'personas', amenazas: 'todas' },
  // Comunes: recursos
  { id: 'extintores', texto: 'Extintores a la distancia y vigentes', dimension: 'recursos', amenazas: 'todas',
    verificacion: deVerificacion('Art. 120', '15.4', 'Art. 32 d)') },
  { id: 'iluminacion', texto: 'Iluminación de emergencia', dimension: 'recursos', amenazas: 'todas',
    verificacion: deVerificacion('Art. 23', '9.1 y 9.2.1') },
  { id: 'senalizacion', texto: 'Señalización de rutas', dimension: 'recursos', amenazas: 'todas' },
  { id: 'alarma', texto: 'Alarma', dimension: 'recursos', amenazas: 'todas' },
  { id: 'botiquin', texto: 'Botiquín', dimension: 'recursos', amenazas: 'todas' },
  // Comunes: sistemas y procesos
  { id: 'plan_vigente', texto: 'Plan vigente', dimension: 'sistemas', amenazas: 'todas' },
  { id: 'rutas_salidas', texto: 'Rutas y salidas con ancho correcto', dimension: 'sistemas', amenazas: 'todas' },
  { id: 'punto_encuentro', texto: 'Punto de encuentro aceptable', dimension: 'sistemas', amenazas: 'todas' },
  { id: 'telefonos', texto: 'Teléfonos de emergencia publicados', dimension: 'sistemas', amenazas: 'todas' },
  // Sismo
  { id: 'sismo_racks', texto: 'Racks y estanterías anclados', dimension: 'recursos', amenazas: ['sismo'] },
  { id: 'sismo_quimicos', texto: 'Químicos con contención contra caída', dimension: 'recursos', amenazas: ['sismo'] },
  { id: 'sismo_altura', texto: 'Objetos en altura asegurados', dimension: 'recursos', amenazas: ['sismo'] },
  { id: 'sismo_estructura', texto: 'Edificación con diseño sismorresistente o evaluación estructural', dimension: 'sistemas', amenazas: ['sismo'] },
  // Inundación
  { id: 'inund_quimicos_altura', texto: 'Químicos sobre el nivel del piso', dimension: 'recursos', amenazas: ['inundacion'] },
  { id: 'inund_subsuelo', texto: 'Sin químicos en subsuelo', dimension: 'recursos', amenazas: ['inundacion'] },
  { id: 'inund_drenajes', texto: 'Drenajes mantenidos', dimension: 'sistemas', amenazas: ['inundacion'] },
  { id: 'inund_antiderrames', texto: 'Kit antiderrames', dimension: 'recursos', amenazas: ['inundacion'] },
  { id: 'inund_ubicacion', texto: 'Ubicación fuera de zona inundable', dimension: 'sistemas', amenazas: ['inundacion'] },
  // Volcán
  { id: 'volcan_ceniza', texto: 'Procedimiento ante caída de ceniza', dimension: 'sistemas', amenazas: ['volcan'] },
  { id: 'volcan_ruta', texto: 'Ruta a zona segura fuera de lahares', dimension: 'sistemas', amenazas: ['volcan'] },
  { id: 'volcan_techos', texto: 'Limpieza de techos', dimension: 'recursos', amenazas: ['volcan'] },
];

export function criteriosDe(amenaza: TipoAmenazaVulnerabilidad): CriterioVulnerabilidad[] {
  return CRITERIOS_VULNERABILIDAD.filter(c => c.amenazas === 'todas' || c.amenazas.includes(amenaza));
}

export const PUNTOS: Record<Exclude<Respuesta, 'no_aplica'>, number> = { cumple: 1, parcial: 0.5, no_cumple: 0 };

// Bandas del punto 17 (iguales a la propuesta de PE-A2): ≥ 90 baja; ≥ 70 media; ≥ 50 alta; si no, muy alta.
export function nivelPorPorcentaje(porcentaje: number): Nivel4 {
  if (!(porcentaje >= 0 && porcentaje <= 100)) throw new Error(`Porcentaje de preparación inválido: ${porcentaje}`);
  if (porcentaje >= 90) return 'bajo';
  if (porcentaje >= 70) return 'medio';
  if (porcentaje >= 50) return 'alto';
  return 'muy_alto';
}

const ORDEN: Nivel4[] = ['bajo', 'medio', 'alto', 'muy_alto'];

// Respuesta de un criterio desde las trazas de la verificación: todas cumplen → cumple; ninguna → no cumple;
// mezcla → parcial. Sin trazas evaluadas (cumple / no cumple), null: lo responde el responsable.
function desdeVerificacion(c: CriterioVulnerabilidad, trazas: Traza[]): { respuesta: Respuesta; trazas: Traza[] } | null {
  if (!c.verificacion) return null;
  const t = trazas.filter(x => c.verificacion!(x) && (x.estado === 'cumple' || x.estado === 'no_cumple'));
  if (t.length === 0) return null;
  const ok = t.filter(x => x.estado === 'cumple').length;
  return { respuesta: ok === t.length ? 'cumple' : ok === 0 ? 'no_cumple' : 'parcial', trazas: t };
}

export interface EntradaVulnerabilidadPorAmenaza {
  amenaza: TipoAmenazaVulnerabilidad;
  nombre?: string;                          // p. ej. "Sismos y terremotos"
  respuestas: Record<string, Respuesta>;    // id del criterio → respuesta del responsable
  verificacion?: Traza[];                   // trazas de verificar() (pe-verificacion.ts)
  ajuste?: { nivel: Nivel4; justificacion: string };
}

export interface DetalleCriterio {
  id: string;
  texto: string;
  dimension: Dimension;
  respuesta: Respuesta;
  origen: 'verificacion' | 'responsable';
  puntos: number | null;  // null = no aplica
}

export interface ResultadoVulnerabilidad {
  amenaza: TipoAmenazaVulnerabilidad;
  porcentaje: number;
  porDimension: Record<Dimension, number | null>;
  nivelCalculado: Nivel4;
  nivel: Nivel4;
  ajustada: boolean;
  detalle: DetalleCriterio[];
  trazas: Traza[];
}

export function vulnerabilidadPorAmenaza(e: EntradaVulnerabilidadPorAmenaza): ResultadoVulnerabilidad {
  const nombre = e.nombre ?? e.amenaza;
  const criterios = criteriosDe(e.amenaza);
  const conocidos = new Set(criterios.map(c => c.id));
  const extra = Object.keys(e.respuestas).filter(id => !conocidos.has(id));
  if (extra.length) throw new Error(`Criterios que no corresponden a "${nombre}": ${extra.join(', ')}`);

  const trazas: Traza[] = [];
  const detalle: DetalleCriterio[] = [];
  const faltan: string[] = [];
  for (const c of criterios) {
    const auto = desdeVerificacion(c, e.verificacion ?? []);
    const respuesta = auto?.respuesta ?? e.respuestas[c.id];
    if (!respuesta) { faltan.push(c.texto); continue; }
    if (!['cumple', 'parcial', 'no_cumple', 'no_aplica'].includes(respuesta)) throw new Error(`Respuesta inválida en "${c.texto}": ${respuesta}`);
    detalle.push({
      id: c.id, texto: c.texto, dimension: c.dimension, respuesta, origen: auto ? 'verificacion' : 'responsable',
      puntos: respuesta === 'no_aplica' ? null : PUNTOS[respuesta],
    });
    if (auto) {
      trazas.push({
        metodo: METODO, norma: auto.trazas.map(t => t.norma).filter((v, i, a) => a.indexOf(v) === i).join(' y '),
        articulo_o_tabla: auto.trazas.map(t => t.articulo_o_tabla).join('; '), umbral: null, valor: respuesta, unidad: '—',
        estado: 'informativo', fuente_doc: auto.trazas.map(t => t.fuente_doc).join(' · '),
        nota: `${nombre}: "${c.texto}" se llenó desde la verificación normativa`,
      });
    }
  }
  if (faltan.length) throw new Error(`Faltan respuestas en "${nombre}": ${faltan.join('; ')}`);

  const cuentan = detalle.filter(d => d.puntos !== null);
  if (cuentan.length === 0) throw new Error(`"${nombre}": todos los criterios son "no aplica"; no se puede calcular`);
  const pct = (ds: DetalleCriterio[]) => (100 * ds.reduce((a, d) => a + (d.puntos as number), 0)) / ds.length;
  const porcentaje = pct(cuentan);
  const porDimension = {} as Record<Dimension, number | null>;
  for (const dim of ['personas', 'recursos', 'sistemas'] as Dimension[]) {
    const ds = cuentan.filter(d => d.dimension === dim);
    porDimension[dim] = ds.length ? pct(ds) : null;
  }
  const nivelCalculado = nivelPorPorcentaje(porcentaje);

  let nivel = nivelCalculado;
  if (e.ajuste) {
    const sube = ORDEN.indexOf(e.ajuste.nivel) - ORDEN.indexOf(nivelCalculado);
    if (sube < 0) throw new Error(`"${nombre}": la vulnerabilidad no se puede bajar (calculada ${nivelCalculado}, pedida ${e.ajuste.nivel})`);
    if (sube > 0) {
      if (!e.ajuste.justificacion || !e.ajuste.justificacion.trim()) {
        throw new Error(`"${nombre}": subir la vulnerabilidad a ${e.ajuste.nivel} requiere justificación`);
      }
      nivel = e.ajuste.nivel;
      trazas.push({
        metodo: METODO, norma: 'Criterio del responsable', articulo_o_tabla: 'Ajuste de la vulnerabilidad (solo hacia arriba)',
        umbral: nivelCalculado, valor: nivel, unidad: '—', estado: 'informativo', fuente_doc: doc.paso0('§7 PE-19'),
        nota: `${nombre}: subida de ${etiquetaNivel(nivelCalculado, 'f')} a ${etiquetaNivel(nivel, 'f')}. Justificación: ${e.ajuste.justificacion.trim()}`,
      });
    }
  }

  trazas.unshift({
    metodo: METODO, norma: 'Criterios de preparación de StorSafe (por validar)', articulo_o_tabla: `Vulnerabilidad por amenaza: ${nombre}`,
    umbral: '≥ 90 baja; 70 a 89,99 media; 50 a 69,99 alta; < 50 muy alta', valor: porcentaje, unidad: '%', estado: 'cumple',
    fuente_doc: doc.paso0('§7 PE-19'),
    nota: `${cuentan.length} criterios evaluados (${detalle.length - cuentan.length} no aplican): vulnerabilidad ${etiquetaNivel(nivelCalculado, 'f')}`,
  });
  trazas.push({
    metodo: METODO, norma: 'Criterios de preparación de StorSafe (por validar)', articulo_o_tabla: 'Revisión',
    umbral: null, valor: null, unidad: '—', estado: 'por_validar', fuente_doc: doc.paso0('§7 PE-19'),
    nota: 'Revisión técnica pendiente',
  });

  return { amenaza: e.amenaza, porcentaje, porDimension, nivelCalculado, nivel, ajustada: nivel !== nivelCalculado, detalle, trazas };
}

// Para la pantalla (PE-A3b): respuesta que la verificación normativa da a un criterio, o null si lo responde
// el responsable.
export function respuestaDesdeVerificacion(criterioId: string, trazas: Traza[]): Respuesta | null {
  const c = CRITERIOS_VULNERABILIDAD.find(x => x.id === criterioId);
  return c ? desdeVerificacion(c, trazas)?.respuesta ?? null : null;
}
